import { Button, TextArea, TextField } from '@pms-digital/design-system';
import { useEffect, useRef, useState } from 'react';

export interface AddDimensionButtonProps {
  onSubmit: (input: { title: string; comment: string }) => void;
  isSubmitting: boolean;
  /**
   * Short French message when the last `POST .../dimensions/generation-jobs`
   * attempt itself failed (network/server error — the disabled-submit gate
   * below already guarantees non-empty input, so this is a transport/server
   * failure, not a validation one). `null` when there is no error to show,
   * mirroring `GenerationPanel`'s `submitError` — the caller derives it from
   * the mutation's `isError`/`error`, which React Query resets on the next
   * `.mutate()` call, so a retry clears a stale message on its own.
   */
  submitError: string | null;
}

/**
 * Mirrors the wizard's "+ Ajouter une dimension" placement/label
 * (dimensions-step.tsx) but as a popover rather than an inline form field —
 * the live matrix grid is a real <table>, so a form spanning 8 palier
 * columns would fight the grid's own layout (point 14).
 */
export function AddDimensionButton({
  onSubmit,
  isSubmitting,
  submitError,
}: AddDimensionButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');

  function close() {
    setIsOpen(false);
    setTitle('');
    setComment('');
  }

  function submit() {
    onSubmit({ title: title.trim(), comment: comment.trim() });
  }

  // The popover is never closed optimistically on click — only once the
  // submit attempt it triggered actually resolves. Closing (and clearing the
  // fields) happens here, the moment `isSubmitting` flips back to `false`
  // with no error: that's a real success, not just "the click handler ran".
  // On failure (`submitError` set), the popover stays open with whatever the
  // user typed still in place, so they can see the error and retry without
  // re-entering title/comment (never silently discard, matching
  // `startGenerationMutation`'s `submitError` precedent in the parent route).
  const wasSubmittingRef = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `close` is a plain function recreated every render (not memoized) — listing it would re-run this effect on every render, defeating the point of watching only `isSubmitting`/`submitError` transitions.
  useEffect(() => {
    const wasSubmitting = wasSubmittingRef.current;
    wasSubmittingRef.current = isSubmitting;
    if (wasSubmitting && !isSubmitting && !submitError) {
      close();
    }
  }, [isSubmitting, submitError]);

  const canSubmit = title.trim().length > 0 && comment.trim().length > 0;

  return (
    <div className="relative">
      {/* Disabled only while a submit is in flight AND the popover is open
          (fix round 3) — otherwise a second click here toggles `isOpen`
          straight to `false`, bypassing `close()` entirely and unmounting
          the popover out from under a still-pending request. Same failure
          mode "Annuler" had (fix round 2), just via this second path: a
          request that then fails has no popover left to show `submitError`
          on, silently losing the user's input and the failure notice.
          Gating on `isOpen` too (not just `isSubmitting`) matters because
          this is a *toggle*, not a plain "open" action — in the one real
          user path, `isSubmitting` only ever becomes true once the popover
          is already open (it's set by this same popover's own "Créer"), so
          this only ever blocks a close-while-submitting click; it never
          blocks *opening* the trigger fresh. */}
      <Button
        type="button"
        variant="accent"
        size="sm"
        compact
        fullWidth
        icon="add"
        disabled={isSubmitting && isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        Ajouter une dimension
      </Button>
      {isOpen && (
        <div className="absolute bottom-full left-0 z-20 mb-1 flex w-80 flex-col gap-3 rounded-md border border-gray-200 bg-white p-4 shadow-lg">
          <TextField
            label="Titre"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            disabled={isSubmitting}
          />
          <TextArea
            label="Commentaire"
            rows={3}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            disabled={isSubmitting}
          />
          {submitError && <p className="text-danger text-small">{submitError}</p>}
          <div className="flex justify-end gap-2">
            {/* Disabled while a submit is in flight (fix round 2) — otherwise
                "Annuler" can unmount the popover and clear title/comment
                out from under a still-pending request; if that request then
                fails, `submitError` has nothing left to render on, silently
                losing both the user's input and the failure notice. */}
            <Button type="button" variant="ghost" size="sm" disabled={isSubmitting} onClick={close}>
              Annuler
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={!canSubmit || isSubmitting}
              onClick={submit}
            >
              Créer
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
